import { createContext, useContext, useState, useEffect } from "react";
import { detectOEMFromDomain, getOEMLogoDataURL, getDefaultBranding } from "../../utils/oemUtils";

const OEMContext = createContext();

export const useOEM = () => {
  const context = useContext(OEMContext);
  if (!context) {
    throw new Error("useOEM must be used within OEMProvider");
  }
  return context;
};

export const OEMProvider = ({ children }) => {
  const [oemConfig, setOemConfig] = useState(null);
  const [logoDataURL, setLogoDataURL] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOEMConfig();
  }, []);

  const loadOEMConfig = async () => {
    try {
      setLoading(true);
      
      // Detect OEM from current domain
      const config = await detectOEMFromDomain();
      setOemConfig(config);

      // Load logo if available
      if (config.oemId && config.oemId !== 'default') {
        const logoURL = await getOEMLogoDataURL(config.oemId);
        setLogoDataURL(logoURL);
      } else {
        setLogoDataURL(null);
      }
    } catch (error) {
      console.error("Error loading OEM config:", error);
      // Fallback to default branding
      setOemConfig(getDefaultBranding());
      setLogoDataURL(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshOEMConfig = () => {
    loadOEMConfig();
  };

  const getBrandName = (language = 'en') => {
    if (!oemConfig) return 'NuagePOS Eagle';
    
    if (oemConfig.translations?.[language]?.brandName) {
      return oemConfig.translations[language].brandName;
    }
    
    return oemConfig.brandName || 'NuagePOS Eagle';
  };

  const getWelcomeMessage = (language = 'en') => {
    if (!oemConfig) return 'Welcome to NuagePOS Eagle';
    
    if (oemConfig.translations?.[language]?.welcomeMessage) {
      return oemConfig.translations[language].welcomeMessage;
    }
    
    return `Welcome to ${oemConfig.brandName}` || 'Welcome to NuagePOS Eagle';
  };

  const value = {
    oemConfig,
    logoDataURL,
    loading,
    refreshOEMConfig,
    getBrandName,
    getWelcomeMessage,
    isDefaultBranding: !oemConfig || oemConfig.oemId === 'default',
  };

  return <OEMContext.Provider value={value}>{children}</OEMContext.Provider>;
};
